import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, receiptUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { STATUS_FLOW, STATUS_META, statusLabel } from "@/lib/status";
import { Plus, Loader2, Tractor, Copy, Phone, Clock, FileText, CheckCircle2, XCircle, Download, Printer } from "lucide-react";

export default function FarmerDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 10;

  useEffect(() => {
    setLoading(true);
    api.get("/applications", { params: { page, limit } })
      .then((r) => { setApps(r.data.items); setTotal(r.data.total); })
      .catch(() => toast.error("Failed to load applications"))
      .finally(() => setLoading(false));
  }, [page]);

  const copyRef = (ref) => { navigator.clipboard.writeText(ref); toast.success("Reference copied"); };

  return (
    <div className="min-h-screen bg-[#f8faf8]">
      <Header />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
          <div>
            <h1 className="font-head font-extrabold text-2xl sm:text-3xl text-emerald-950">Namaste, {user?.name || "Farmer"} 🌾</h1>
            <p className="mt-1.5 text-slate-500">Track your subsidy applications and their status here.</p>
          </div>
          <Button className="rounded-full bg-brand hover:bg-emerald-800 text-white h-11 px-6" onClick={() => navigate("/apply")} data-testid="new-application-button">
            <Plus className="mr-1.5 h-4 w-4" /> New Application
          </Button>
        </div>

        {loading ? (
          <div className="grid place-items-center py-20"><Loader2 className="h-8 w-8 animate-spin text-emerald-700" /></div>
        ) : apps.length === 0 ? (
          <Card className="rounded-3xl border-emerald-100 border-dashed p-12 text-center" data-testid="empty-state">
            <div className="h-16 w-16 mx-auto rounded-2xl bg-emerald-50 grid place-items-center"><Tractor className="h-8 w-8 text-emerald-600" /></div>
            <h3 className="mt-5 font-head font-bold text-xl text-emerald-950">No applications yet</h3>
            <p className="mt-2 text-slate-500 max-w-sm mx-auto">Start your tractor subsidy application — we'll guide you through every step.</p>
            <Button className="mt-6 rounded-full bg-brand hover:bg-emerald-800 text-white" onClick={() => navigate("/apply")}>Start Application <Plus className="ml-1.5 h-4 w-4" /></Button>
          </Card>
        ) : (
          <>
            <div className="space-y-6" data-testid="applications-list">
              {apps.map((a) => <AppCard key={a.id} app={a} onCopy={copyRef} />)}
            </div>
            {total > limit && (
              <div className="mt-8 flex items-center justify-between" data-testid="farmer-pagination">
                <span className="text-xs text-slate-500" data-testid="farmer-page-info">Showing {(page - 1) * limit + 1}–{Math.min(page * limit, total)} of {total}</span>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" className="rounded-full border-emerald-200 text-emerald-700 h-9" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="farmer-prev-page">Previous</Button>
                  <Button variant="outline" size="sm" className="rounded-full border-emerald-200 text-emerald-700 h-9" disabled={page * limit >= total} onClick={() => setPage((p) => p + 1)} data-testid="farmer-next-page">Next</Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

const AppCard = ({ app, onCopy }) => {
  const meta = STATUS_META[app.status] || {};
  const rejected = app.status === "rejected";
  const currentIdx = STATUS_FLOW.indexOf(app.status);
  const latestRemark = app.remarks?.[app.remarks.length - 1];

  return (
    <Card className="rounded-3xl border-emerald-100 p-6 sm:p-7 shadow-sm" data-testid="application-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <span className="font-head font-bold text-lg text-emerald-950">{app.scheme_name || app.scheme}</span>
            <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border ${meta.cls}`} data-testid="farmer-application-status-badge">{statusLabel(app.status)}</span>
          </div>
          <button onClick={() => onCopy(app.number)} className="mt-1.5 inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-emerald-700 font-mono" data-testid="copy-reference">
            {app.number} <Copy className="h-3.5 w-3.5" />
          </button>
        </div>
        <div className="text-right text-xs text-slate-400 flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> {new Date(app.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</div>
      </div>

      <div className="mt-3 grid sm:grid-cols-3 gap-3 text-sm">
        <Info label="Applicant" value={app.applicant?.full_name} />
        <Info label="Mobile" value={app.applicant?.mobile || app.user_phone} />
        <Info label="Submitted" value={new Date(app.created_at).toLocaleDateString("en-IN")} />
      </div>

      {/* Timeline */}
      {rejected ? (
        <div className="mt-5 rounded-2xl bg-rose-50 border border-rose-200 p-4 flex gap-3">
          <XCircle className="h-5 w-5 text-rose-600 shrink-0" />
          <div>
            <div className="font-semibold text-rose-700 text-sm">Application Rejected</div>
            {latestRemark?.remark && <div className="text-sm text-rose-600 mt-0.5">{latestRemark.remark}</div>}
          </div>
        </div>
      ) : (
        <div className="mt-6">
          <div className="flex items-center">
            {STATUS_FLOW.map((s, i) => (
              <React.Fragment key={s}>
                <div className="flex flex-col items-center flex-1">
                  <div className={`h-8 w-8 rounded-full grid place-items-center border-2 ${i <= currentIdx ? "bg-emerald-600 border-emerald-600 text-white" : "bg-white border-slate-200 text-slate-300"}`}>
                    {i < currentIdx ? <CheckCircle2 className="h-4 w-4" /> : <span className="text-xs font-bold">{i + 1}</span>}
                  </div>
                  <span className={`mt-1.5 text-[10px] sm:text-[11px] text-center leading-tight ${i <= currentIdx ? "text-emerald-700 font-medium" : "text-slate-400"}`}>{statusLabel(s)}</span>
                </div>
                {i < STATUS_FLOW.length - 1 && <div className={`h-0.5 flex-1 -mt-5 ${i < currentIdx ? "bg-emerald-500" : "bg-slate-200"}`} />}
              </React.Fragment>
            ))}
          </div>
        </div>
      )}

      {latestRemark?.remark && !rejected && (
        <div className="mt-5 rounded-2xl bg-slate-50 border border-slate-100 p-4 text-sm text-slate-600 flex gap-2.5">
          <FileText className="h-4 w-4 text-slate-400 shrink-0 mt-0.5" /> <span><strong className="text-slate-700">Officer note:</strong> {latestRemark.remark}</span>
        </div>
      )}

      <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm text-emerald-700">
          <Phone className="h-4 w-4" /> Helpline: <a href="tel:+918762765882" className="font-semibold hover:underline">+91 87627 65882</a>
        </div>
        <a href={receiptUrl(app.id)} target="_blank" rel="noreferrer" data-testid="download-receipt">
          <Button variant="outline" size="sm" className="rounded-full border-emerald-200 text-emerald-700 h-9">
            <Download className="h-4 w-4 mr-1.5" /> Download PDF
          </Button>
        </a>
        <Button variant="outline" size="sm" className="rounded-full border-emerald-200 text-emerald-700 h-9" onClick={() => window.open(receiptUrl(app.id), "_blank")} data-testid="print-application">
          <Printer className="h-4 w-4 mr-1.5" /> Print
        </Button>
      </div>
    </Card>
  );
};

const Info = ({ label, value }) => (
  <div className="rounded-xl bg-[#f1f6f2] px-3.5 py-2.5">
    <div className="text-[11px] uppercase tracking-wide text-slate-400">{label}</div>
    <div className="text-slate-700 font-medium truncate">{value || "—"}</div>
  </div>
);
